import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import apiHelper from '../apiHelper.js';
import EmpltyBoxIcon from '../assets/img/brand/box.png';

const STOCK_LIMIT = 50;
const STOCK_ENDPOINT = 'inventory/stocksminilist/';

function mapStockItem(item) {
    return {
        id: item.stock_id,
        title: item.spu_name,
        sku: item.sku_name,
        sku_code: item.sku_code,
        openId: item.sku_uuid,
        condition: item.condition || 'NA',
        condition_type: item.condition_type || 'NA',
        status: item.status ? 'Active' : 'Inactive',
        is_archived: item.is_archived,
        listed: item.listed || false,
    };
}

export default function StockSearchDropdown({ selected, onSelect, onClear, onClose, error, disabled }) {
    const [open, setOpen]           = useState(false);
    const [query, setQuery]         = useState('');
    const [items, setItems]         = useState([]);
    const [loading, setLoading]     = useState(false);
    const [hasMore, setHasMore]     = useState(true);
    const [hoveredId, setHoveredId] = useState(null);
    const skipRef                   = useRef(0);
    const abortRef                  = useRef(null);
    const containerRef              = useRef(null);
    const inputRef                  = useRef(null);
    const queryRef                  = useRef(query);
    useEffect(() => { queryRef.current = query; }, [query]);

    const closeDropdown = useCallback(() => {
        setOpen(false);
        if (onClose) onClose();
    }, [onClose]);

    // Close on outside click
    useEffect(() => {
        const h = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                closeDropdown();
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [closeDropdown]);

    // Focus inline input when open
    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    const fetchPage = useCallback(async (searchQuery, currentSkip, reset) => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);
        try {
            const params = new URLSearchParams({
                archived: 'false',
                skip: currentSkip,
                limit: STOCK_LIMIT,
            });
            if (searchQuery) params.set('search', searchQuery);
            const res = await apiHelper(`${STOCK_ENDPOINT}?${params}`, 'GET');
            if (!res?.ok) return;
            const data = await res.json();
            const results = (data.results || []).map(mapStockItem);
            setItems(prev => reset ? results : [...prev, ...results]);
            setHasMore(results.length === STOCK_LIMIT);
            skipRef.current = currentSkip + results.length;
        } catch (err) {
            if (err.name !== 'AbortError') console.error('Stock fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load first page on open
    useEffect(() => {
        if (!open) return;
        setItems([]);
        skipRef.current = 0;
        setHasMore(true);
        fetchPage(query, 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Debounced API search on query change
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => {
            setItems([]);
            skipRef.current = 0;
            setHasMore(true);
            fetchPage(query, 0, true);
        }, 300);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 80 && !loading && hasMore) {
            fetchPage(queryRef.current, skipRef.current, false);
        }
    };

    const handleSelect = (opt) => {
        onSelect(opt);
        setOpen(false);
        setQuery('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onClear();
        setQuery('');
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            {/* Trigger box */}
            <div
                className="form-control common-input-all d-flex justify-between items-center cursor-pointer rounded p-2"
                onClick={() => { if (!disabled) setOpen(prev => !prev); }}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '40px',
                    border: `1px solid ${error ? '#DC3545' : '#6C757D'}`,
                    cursor: disabled ? 'not-allowed' : 'text',
                }}
            >
                {selected ? (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <span style={{ fontSize: '14px', color: '#212529' }}>{selected.title}</span>
                        <div style={{ fontSize: '12px', color: '#6C757D', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', lineHeight: 'normal' }}>
                                <span>SKU:</span>
                                <span>{selected.sku}</span>
                            </span>
                            <span style={{ margin: '0 6px' }}>|</span>
                            <span style={{ background: '#FFEB00', borderRadius: '99px', fontSize: '8px', fontWeight: 600, color: '#212529', padding: '2px 5px', marginRight: '5px', lineHeight: 'normal' }}>
                                {selected.condition}
                            </span>
                            <span className="status" style={{ minWidth: 'unset', padding: '2px 5px', fontSize: '8px', lineHeight: 'normal', background: '#FFEB00', borderRadius: '99px', color: '#212529', fontWeight: '600' }}>
                                {selected.condition_type}
                            </span>
                        </div>
                    </div>
                ) : (
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
                        onClick={e => e.stopPropagation()}
                        onFocus={() => { if (!disabled) setOpen(true); }}
                        placeholder="Search and Select"
                        disabled={disabled}
                        style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px', background: 'transparent' }}
                    />
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {selected && (
                        <span onClick={handleClear} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M9 3L3 9M3 3L9 9" stroke="#212529" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    )}
                    <ChevronDown size={16} />
                </div>
            </div>

            {/* Dropdown list */}
            {open && !disabled && (
                <div
                    onScroll={handleScroll}
                    className="z-10 w-full bg-white border rounded shadow-lg mt-1"
                    style={{ maxHeight: '320px', overflowY: 'auto', position: 'absolute', right: 0, left: 0, width: '100%', zIndex: 100 }}
                >
                    {items.map((opt) => (
                        <div
                            key={opt.id}
                            className="p-2 flex flex-col cursor-pointer"
                            onClick={() => handleSelect(opt)}
                            onMouseEnter={() => setHoveredId(opt.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            style={{ background: hoveredId === opt.id ? '#F8F9FA' : 'white' }}
                        >
                            <span style={{ fontSize: '14px', color: '#212529', display: 'block' }}>{opt.title}</span>
                            <div style={{ fontSize: '12px', color: '#6C757D', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', lineHeight: 'normal' }}>
                                    <span>SKU:</span>
                                    <span>{opt.sku}</span>
                                </span>
                                <span style={{ margin: '0 6px' }}>|</span>
                                <span style={{ background: '#FFEB00', borderRadius: '99px', fontSize: '8px', fontWeight: 600, color: '#212529', padding: '2px 5px', marginRight: '5px', lineHeight: 'normal' }}>
                                    {opt.condition}
                                </span>
                                <span className="status" style={{ minWidth: 'unset', padding: '2px 5px', fontSize: '8px', lineHeight: 'normal', background: '#FFEB00', borderRadius: '99px', color: '#212529', fontWeight: '600' }}>
                                    {opt.condition_type}
                                </span>
                            </div>
                        </div>
                    ))}

                    {items.length === 0 && !loading && (
                        <div style={{ padding: '24px 12px', textAlign: 'center', color: '#6C757D', fontSize: 14 }}>
                            <img src={EmpltyBoxIcon} alt="No results" style={{ width: 48, height: 48, marginBottom: 8, opacity: 0.6 }} />
                            <div>No Results</div>
                        </div>
                    )}

                    {loading && (
                        <div style={{ textAlign: 'center', padding: 8, color: '#999', fontSize: 12, fontFamily: 'Montserrat' }}>
                            Loading...
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
